import axios from 'axios';
import { searchApiBaseUrl } from '../config';
import { getValidSearchApiToken } from './searchApiAuth';

/**
 * Response model for Search API company search
 */
export const searchApiCompanyResponseSchema = {
  status: null,
  message: null,
  correlationId: null,
  data: {
    results: [
      {
        companyId: null,
        name: null,
        city: null,
        postalCode: null,
        latitude: null,
        longitude: null,
        organizationNumber: null,
        professions: [],
        licenseType: null,
        totalJobs: null,
        logoUrl: null,
        hasProfileSearchAddOn: false,
        franchiseeCompanies: [
          {
            id: null,
            name: null,
          },
        ],
        franchisorCompanies: [
          {
            id: null,
            name: null,
          },
        ],
        matchType: null,
      },
    ],
    totalCount: null,
    from: null,
    size: null,
    query: null,
    searchTime: null,
  },
};

/**
 * Gets companies by their IDs from the Search API
 * @param {string[]} companyIds - Array of company IDs
 * @returns {Promise<[Error, Object]>} Tuple of [error, response]
 */
export const getCompaniesByIds = async (companyIds) => {
  const axiosWithoutDefaults = new axios.Axios({
    headers: Object.create(null), // absolutely empty
  });
  if (!companyIds || !Array.isArray(companyIds) || companyIds.length === 0) {
    return [
      new Error('Company IDs array is required and cannot be empty'),
      null,
    ];
  }

  try {
    // Get valid token (will refresh if needed)
    const [tokenError, token] = await getValidSearchApiToken();
    if (tokenError || !token) {
      return [
        tokenError || new Error('Failed to obtain Search API token'),
        null,
      ];
    }

    // Format company IDs as comma-separated string
    const idsQuery = companyIds.join(',');
    // Make request to Search API
    const response = await axiosWithoutDefaults.get(
      `${searchApiBaseUrl}/api/v1/Companies/by-ids`,
      {
        params: {
          ids: idsQuery,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return [null, JSON.parse(response.data).data.results];
  } catch (error) {
    // Handle 401 Unauthorized - token might be invalid, try refreshing
    if (error.response && error.response.status === 401) {
      console.warn('Search API token expired, refreshing...');
      const [refreshError, newToken] = await getValidSearchApiToken();

      if (!refreshError && newToken) {
        // Retry the request with new token
        try {
          const idsQuery = companyIds.join(',');
          const retryResponse = await axiosWithoutDefaults.get(
            `${searchApiBaseUrl}/api/v1/Companies/by-ids`,
            {
              params: {
                ids: idsQuery,
              },
              headers: {
                Authorization: `Bearer ${newToken}`,
              },
            }
          );
          return [null, JSON.parse(retryResponse.data).data.results];
        } catch (retryError) {
          return [retryError, null];
        }
      }
    }

    return [error, null];
  }
};
